import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

const VisualDevCaseStudyModal = ({ project, slideIndex, onSlideIndexChange, onClose }) => {
  const slides = useMemo(() => {
    const raw = project?.slides;
    if (!Array.isArray(raw) || raw.length === 0) return [];
    return raw.map((s, i) => ({
      src: typeof s === "string" ? s : s.src,
      alt: (typeof s === "object" && s.alt) || `${project?.title || "Project"} — slide ${i + 1}`,
    }));
  }, [project]);

  const n = slides.length;
  const idx = n ? Math.min(Math.max(0, slideIndex), n - 1) : 0;
  const slide = slides[idx];
  const canPrev = n > 0 && idx > 0;
  const canNext = n > 0 && idx < n - 1;

  useEffect(() => {
    if (!project || n === 0) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft" && idx > 0) onSlideIndexChange?.(idx - 1);
      if (e.key === "ArrowRight" && idx < n - 1) onSlideIndexChange?.(idx + 1);
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [project, n, idx, onClose, onSlideIndexChange]);

  const topChromeRef = useRef(null);
  const [topChromePx, setTopChromePx] = useState(56);

  useLayoutEffect(() => {
    const el = topChromeRef.current;
    if (!el) return;
    const sync = () => setTopChromePx(el.offsetHeight);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [project?.id, idx]);

  if (!project || !slide) return null;

  const navBtn =
    "text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors disabled:cursor-not-allowed disabled:opacity-30 enabled:hover:text-[#ffa94c] focus:outline-none focus-visible:text-[#ffa94c]";

  /** Image starts below measured top bar; max height matches remaining viewport above bottom padding. */
  const imgStyle = {
    maxHeight: `calc(100vh - ${topChromePx}px - 6.5rem)`,
    maxWidth: "100vw",
    width: "auto",
    height: "auto",
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${project.title} case study`}
      className="fixed inset-0 z-[9999] overflow-hidden bg-black"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      {/* Image plane — top inset matches header bar so boards are not hidden under it */}
      <div
        className="absolute inset-0 z-0 flex items-start justify-center overflow-y-auto px-2 md:px-4"
        style={{ paddingTop: topChromePx, paddingBottom: "6.5rem" }}
      >
        <img
          src={encodeURI(slide.src)}
          alt={slide.alt}
          className="max-w-full object-contain object-top"
          style={imgStyle}
          draggable={false}
        />
      </div>

      <div className="pointer-events-none absolute inset-0 z-10 flex flex-col justify-between">
        <div
          ref={topChromeRef}
          className="pointer-events-auto flex shrink-0 items-center justify-between border-b border-[#25282c] bg-black/80 px-4 py-3 backdrop-blur-sm md:px-6"
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-gray-300">
            {project.title}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-white hover:text-[#ffa94c] focus:outline-none focus-visible:text-[#ffa94c]"
          >
            Close
          </button>
        </div>

        <div className="pointer-events-auto flex items-center justify-between border-t border-[#25282c] bg-black/90 px-4 py-4 backdrop-blur-sm md:px-8 md:py-5">
          <button
            type="button"
            className={navBtn}
            disabled={!canPrev}
            onClick={() => canPrev && onSlideIndexChange?.(idx - 1)}
            aria-label="Previous slide"
          >
            &lt; PREV
          </button>
          <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-gray-600">
            {idx + 1} / {n}
          </span>
          <button
            type="button"
            className={navBtn}
            disabled={!canNext}
            onClick={() => canNext && onSlideIndexChange?.(idx + 1)}
            aria-label="Next slide"
          >
            NEXT &gt;
          </button>
        </div>
      </div>
    </div>
  );
};

export default VisualDevCaseStudyModal;
